import { ItemTile } from '@/components/auctions/item-tile';
import { Layout } from '@/components/layouts/layout';
import {
    useAuctionWithItems,
    useSyncAllSold,
    useSyncAuctionItem,
    useUpdateAuctionItem,
} from '@/hooks/use-auctions';
import { formatAuctionMoney, type AuctionItem } from '@/types/auctions';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Check, ChevronDown, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/auctions/$auctionId/review')({
    component: ReviewPage,
});

function ReviewPage() {
    const { auctionId } = Route.useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useAuctionWithItems(auctionId);
    const updateItem = useUpdateAuctionItem(auctionId);
    const syncItem = useSyncAuctionItem(auctionId);
    const syncAll = useSyncAllSold(auctionId);
    const [buyersOpen, setBuyersOpen] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const items = data?.items ?? [];
    const platform = data?.platform ?? 'Other';

    const { sold, noSale, unresolved, revenue, syncedCount } = useMemo(() => {
        let revenue = 0;
        let syncedCount = 0;
        const sold: AuctionItem[] = [];
        const noSale: AuctionItem[] = [];
        const unresolved: AuctionItem[] = [];
        items.forEach((i) => {
            if (i.status === 'sold') {
                sold.push(i);
                revenue += i.sale_price_cents ?? 0;
                if (i.synced_to_inventory) syncedCount++;
            } else if (i.status === 'no_sale') {
                noSale.push(i);
            } else {
                unresolved.push(i);
            }
        });
        const soldSorted = [...sold].sort(
            (a, b) => (a.sold_order ?? 0) - (b.sold_order ?? 0),
        );
        return { sold: soldSorted, noSale, unresolved, revenue, syncedCount };
    }, [items]);

    const buyerGroups = useMemo(() => {
        const map: Record<
            string,
            { buyer: string; items: number; total: number }
        > = {};
        sold.forEach((item) => {
            const key = item.buyer_username || '(no username)';
            if (!map[key]) map[key] = { buyer: key, items: 0, total: 0 };
            map[key].items++;
            map[key].total += item.sale_price_cents ?? 0;
        });
        return Object.values(map).sort((a, b) => b.total - a.total);
    }, [sold]);

    function handleResolve(id: string, status: 'sold' | 'no_sale') {
        const soldOrder = status === 'sold' ? sold.length + 1 : undefined;
        updateItem.mutate(
            {
                id,
                input: {
                    status,
                    ...(status === 'sold'
                        ? {
                              sold_at: new Date().toISOString(),
                              sold_order: soldOrder,
                          }
                        : {}),
                },
            },
            {
                onError: () => toast.error('Failed to update'),
            },
        );
    }

    function handlePriceChange(id: string, value: string) {
        const cents = value ? Math.round(parseFloat(value) * 100) : null;
        updateItem.mutate({ id, input: { sale_price_cents: cents } });
    }

    function handleBuyerChange(id: string, value: string) {
        updateItem.mutate({ id, input: { buyer_username: value || null } });
    }

    function handleSyncItem(item: AuctionItem) {
        syncItem.mutate(
            { item, platform },
            {
                onSuccess: () => toast.success('Synced to inventory'),
                onError: () => toast.error('Failed to sync'),
            },
        );
    }

    function handleSyncAll() {
        syncAll.mutate(platform, {
            onSuccess: () => toast.success('All sold items synced'),
            onError: () => toast.error('Sync failed'),
        });
    }

    if (isLoading) {
        return (
            <Layout>
                <div className="max-w-6xl mx-auto px-4 py-8">
                    <div className="h-8 w-48 rounded animate-pulse bg-[#111726]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
                {/* Back */}
                <button
                    onClick={() => navigate({ to: '/auctions' })}
                    className="flex items-center gap-1 text-[13px] font-semibold mb-3.5 cursor-pointer text-[#9aa2c0]"
                >
                    <ArrowLeft size={16} /> Auctions
                </button>

                {/* Header */}
                <div className="flex items-end justify-between mb-5.5">
                    <div>
                        <h1 className="text-[30px] font-bold tracking-tight mb-1.5 [font-family:var(--font-heading)] text-[#f5f7fb]">
                            Show Results
                        </h1>
                        <p className="text-[#9aa2c0] text-[14.5px]">
                            {data?.title} · review and sync sold items back to
                            inventory.
                        </p>
                    </div>
                    <button
                        onClick={handleSyncAll}
                        disabled={
                            syncAll.isPending ||
                            sold.every((s) => s.synced_to_inventory) ||
                            sold.some(
                                (s) =>
                                    !s.synced_to_inventory &&
                                    ((s.sale_price_cents ?? 0) === 0 ||
                                        !s.buyer_username),
                            )
                        }
                        title={
                            sold.some(
                                (s) =>
                                    !s.synced_to_inventory &&
                                    ((s.sale_price_cents ?? 0) === 0 ||
                                        !s.buyer_username),
                            )
                                ? 'All sold items need a price and buyer username before syncing'
                                : undefined
                        }
                        className="flex items-center gap-2 font-semibold text-[14px] text-white px-4.5 py-2.75 rounded-[10px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-[#6c4af0] hover:bg-[#7d5cff]"
                    >
                        <RefreshCw size={14} />
                        {syncAll.isPending ? 'Syncing…' : 'Sync All Sold'}
                    </button>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-4 gap-3.5 mb-6.5">
                    {[
                        {
                            label: 'Total items',
                            value: items.length,
                            color: '#f5f7fb',
                            border: '#232b3f',
                        },
                        {
                            label: 'Sold',
                            value: sold.length,
                            sub: formatAuctionMoney(revenue),
                            color: '#34d27f',
                            border: 'rgba(52,210,127,.25)',
                        },
                        {
                            label: 'No sale',
                            value: noSale.length,
                            color: '#9aa2c0',
                            border: '#232b3f',
                        },
                        {
                            label: 'Unresolved',
                            value: unresolved.length,
                            color:
                                unresolved.length > 0 ? '#f5a524' : '#f5f7fb',
                            border:
                                unresolved.length > 0
                                    ? 'rgba(245,165,36,.3)'
                                    : '#232b3f',
                        },
                    ].map((stat) => (
                        <div
                            key={stat.label}
                            className="rounded-[14px] p-4.5 bg-[#111726]"
                            style={{ border: `1px solid ${stat.border}` }}
                        >
                            <div className="text-[12.5px] mb-2 text-[#9aa2c0]">
                                {stat.label}
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span
                                    className="text-[30px] font-bold [font-family:var(--font-heading)]"
                                    style={{ color: stat.color }}
                                >
                                    {stat.value}
                                </span>
                                {stat.sub && (
                                    <span className="text-[14px] font-semibold font-mono text-[#34d27f]">
                                        {stat.sub}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Unresolved */}
                {unresolved.length > 0 && (
                    <div className="rounded-2xl p-4.5 mb-4.5 bg-[#111726] border border-[rgba(245,165,36,.3)]">
                        <div className="flex items-center gap-2 mb-1">
                            <svg
                                width={13}
                                height={13}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#f5a524"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 3 2 20h20L12 3zM12 9v5M12 17v.01" />
                            </svg>
                            <span className="font-semibold text-[17px] [font-family:var(--font-heading)] text-[#f5f7fb]">
                                Unresolved items
                            </span>
                        </div>
                        <p className="text-[12.5px] mb-3.5 text-[#9aa2c0]">
                            Skipped or still queued when the auction ended —
                            mark each before syncing.
                        </p>
                        <div className="flex flex-col gap-2">
                            {unresolved.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-3 p-2.5 rounded-[11px] bg-[#0d1322] border border-[#1a2235]"
                                >
                                    <div className="w-10.5 h-10.5 shrink-0 rounded-[9px] overflow-hidden">
                                        <ItemTile
                                            item={item.inventory_items}
                                            borderRadius={9}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[14px] font-semibold text-[#e7eaf3]">
                                            {item.inventory_items
                                                ?.description ?? '—'}
                                        </div>
                                        <div className="text-[12px] text-[#6b7390]">
                                            {item.inventory_items?.brand} ·{' '}
                                            {item.inventory_items?.size} ·{' '}
                                            {item.status === 'skipped'
                                                ? 'Skipped during show'
                                                : 'Never came up'}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleResolve(item.id, 'sold')
                                        }
                                        className="text-[12.5px] font-semibold px-3.25 py-2 rounded-[9px] cursor-pointer shrink-0 text-[#34d27f] bg-[rgba(52,210,127,.12)] border border-[rgba(52,210,127,.3)]"
                                    >
                                        Sold
                                    </button>
                                    <button
                                        onClick={() =>
                                            handleResolve(item.id, 'no_sale')
                                        }
                                        className="text-[12.5px] font-semibold px-3.25 py-2 rounded-[9px] cursor-pointer shrink-0 text-[#9aa2c0] bg-[#141b2c] border border-[#2a3450]"
                                    >
                                        No sale
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Sold list */}
                <div className="rounded-2xl p-4.5 mb-4.5 bg-[#111726] border border-[#232b3f]">
                    <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-[17px] [font-family:var(--font-heading)] text-[#f5f7fb]">
                            Sold
                        </span>
                        <span className="text-[12px] font-semibold text-[#6b7390]">
                            in order of sale
                        </span>
                    </div>
                    <p className="text-[12.5px] mb-3.5 text-[#9aa2c0]">
                        {sold.length} items · {formatAuctionMoney(revenue)}{' '}
                        total · {syncedCount} synced
                    </p>
                    {sold.length === 0 ? (
                        <p className="text-center py-8 text-[13px] text-[#5d667e]">
                            No sold items yet.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {sold.map((item) => {
                                const priceVal =
                                    item.sale_price_cents != null
                                        ? String(item.sale_price_cents / 100)
                                        : '';
                                const hasPrice =
                                    (item.sale_price_cents ?? 0) > 0;
                                return (
                                    <div
                                        key={item.id}
                                        className="flex items-center gap-3.25 p-2.5 rounded-[11px] bg-[#0d1322] border border-[#1a2235]"
                                    >
                                        {/* Order badge */}
                                        <span className="w-6.5 h-6.5 shrink-0 flex items-center justify-center rounded-xl text-[12px] font-bold font-mono bg-[rgba(108,74,240,.14)] text-[#9d86ff]">
                                            #{item.sold_order ?? '—'}
                                        </span>

                                        {/* Tile */}
                                        <div className="w-11.5 h-11.5 shrink-0 rounded-[10px] overflow-hidden">
                                            <ItemTile
                                                item={item.inventory_items}
                                                borderRadius={10}
                                            />
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[14px] font-semibold truncate text-[#e7eaf3]">
                                                {item.inventory_items
                                                    ?.description ?? '—'}
                                            </div>
                                            <div className="text-[12px] text-[#6b7390]">
                                                {item.inventory_items?.brand} ·{' '}
                                                {item.inventory_items?.size}
                                            </div>
                                        </div>

                                        {/* Price input */}
                                        <div
                                            className={`flex items-center gap-1.5 rounded-[9px] px-2.75 shrink-0 w-27 bg-[#080b14] border ${hasPrice ? 'border-[#2a3450]' : 'border-[rgba(245,165,36,.5)]'}`}
                                        >
                                            <span className="text-[14px] font-bold text-[#34d27f]">
                                                $
                                            </span>
                                            <input
                                                inputMode="decimal"
                                                defaultValue={priceVal}
                                                onBlur={(e) =>
                                                    handlePriceChange(
                                                        item.id,
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="0"
                                                className="w-full bg-transparent border-none outline-none text-[15px] font-bold py-2.25 font-mono text-[#f5f7fb]"
                                            />
                                        </div>

                                        {/* Buyer input */}
                                        <div className="flex items-center gap-1 rounded-[9px] px-2.75 shrink-0 w-37.5 bg-[#080b14] border border-[#2a3450]">
                                            <span className="text-[14px] text-[#5d667e]">
                                                @
                                            </span>
                                            <input
                                                defaultValue={
                                                    item.buyer_username ?? ''
                                                }
                                                onBlur={(e) =>
                                                    handleBuyerChange(
                                                        item.id,
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="username"
                                                className="w-full bg-transparent border-none outline-none text-[13.5px] py-2.5 text-[#f5f7fb]"
                                            />
                                        </div>

                                        {/* Sync button */}
                                        <div className="shrink-0 w-24 flex justify-end">
                                            {item.synced_to_inventory ? (
                                                <span className="flex items-center gap-1 text-[12px] font-semibold px-2.75 py-2 rounded-xl text-[#34d27f] bg-[rgba(52,210,127,.1)] opacity-85">
                                                    <Check
                                                        size={12}
                                                        strokeWidth={2.5}
                                                    />{' '}
                                                    Synced
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        handleSyncItem(item)
                                                    }
                                                    disabled={
                                                        syncItem.isPending ||
                                                        !hasPrice ||
                                                        !item.buyer_username
                                                    }
                                                    title={
                                                        !hasPrice
                                                            ? 'Enter a price before syncing'
                                                            : !item.buyer_username
                                                              ? 'Enter a buyer username before syncing'
                                                              : undefined
                                                    }
                                                    className="flex items-center gap-1 text-[12.5px] font-semibold px-3 py-2 rounded-[9px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-[#9d86ff] bg-[rgba(108,74,240,.12)] border border-[rgba(108,74,240,.3)]"
                                                >
                                                    <RefreshCw size={12} /> Sync
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* No sale list */}
                {noSale.length > 0 && (
                    <div className="rounded-2xl p-4.5 mb-4.5 bg-[#111726] border border-[#232b3f]">
                        <h3 className="font-semibold text-[17px] mb-3.5 [font-family:var(--font-heading)] text-[#f5f7fb]">
                            No sale{' '}
                            <span className="text-[#6b7390] font-normal">
                                ({noSale.length})
                            </span>
                        </h3>
                        <div className="flex flex-col gap-2">
                            {noSale.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-3 p-2.5 rounded-[11px] bg-[#0d1322] border border-[#1a2235]"
                                >
                                    <div className="w-10.5 h-10.5 shrink-0 rounded-[9px] overflow-hidden opacity-70">
                                        <ItemTile
                                            item={item.inventory_items}
                                            borderRadius={9}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[14px] font-semibold text-[#cdd3e6]">
                                            {item.inventory_items
                                                ?.description ?? '—'}
                                        </div>
                                        <div className="text-[12px] text-[#6b7390]">
                                            Returns to inventory as-is
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Buyer breakdown */}
                <div className="rounded-2xl overflow-hidden bg-[#111726] border border-[#232b3f]">
                    <button
                        onClick={() => setBuyersOpen((o) => !o)}
                        className="w-full flex items-center justify-between p-4.5 cursor-pointer"
                    >
                        <span className="font-semibold text-[17px] [font-family:var(--font-heading)] text-[#f5f7fb]">
                            Buyer breakdown{' '}
                            <span className="text-[#6b7390] font-normal font-[inherit] text-[15px]">
                                ({buyerGroups.length})
                            </span>
                        </span>
                        <ChevronDown
                            size={16}
                            className={`text-[#9aa2c0] transition-transform duration-200 ${buyersOpen ? 'rotate-180' : 'rotate-0'}`}
                        />
                    </button>
                    {buyersOpen && (
                        <div className="px-4.5 pb-4.5 flex flex-col gap-2">
                            {buyerGroups.length === 0 ? (
                                <p className="text-[13px] pb-4 text-[#5d667e]">
                                    No buyer usernames recorded yet.
                                </p>
                            ) : (
                                buyerGroups.map((group) => (
                                    <div
                                        key={group.buyer}
                                        className="flex items-center justify-between px-3.75 py-3.25 rounded-[11px] bg-[#0d1322] border border-[#1a2235]"
                                    >
                                        <div className="flex items-center gap-2.75">
                                            <div className="w-8.5 h-8.5 rounded-full flex items-center justify-center text-[12px] font-bold text-white shrink-0 bg-[linear-gradient(135deg,#6c4af0,#9d86ff)]">
                                                {group.buyer
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="text-[14px] font-semibold text-[#e7eaf3]">
                                                    @{group.buyer}
                                                </div>
                                                <div className="text-[11.5px] text-[#6b7390]">
                                                    {group.items} item
                                                    {group.items > 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <span className="text-[16px] font-bold font-mono text-[#34d27f]">
                                            {formatAuctionMoney(group.total)}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
